import { useState, useEffect } from "react";
import { Trophy, TrendingUp, TrendingDown, Sparkles, X, Share2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function buildShareText(trend) {
  const { current_rank, prev_rank, rank_change, total_reps, cash_change_pct } = trend;
  const period = trend.period_days === 7 ? "this week" : `the last ${trend.period_days} days`;

  if (rank_change > 0) {
    const cashSuffix = cash_change_pct > 0 ? ` (+${cash_change_pct}% cash)` : "";
    return `Just climbed from #${prev_rank} → #${current_rank} on WarMap ${period}${cashSuffix} 🏆`;
  }
  if (rank_change === 0 && current_rank <= 3) {
    return `Holding the #${current_rank} spot on WarMap ${period} 💪 Top ${current_rank} of ${total_reps}`;
  }
  if (cash_change_pct > 0) {
    return `Cash collected up +${cash_change_pct}% on WarMap ${period} 📈`;
  }
  if (prev_rank === null || prev_rank === undefined) {
    return `New on the WarMap leaderboard at #${current_rank} of ${total_reps} ${period} — let's go 🚀`;
  }
  return `Locked in at #${current_rank} of ${total_reps} on WarMap ${period} — climbing next week 💪`;
}

function buildHeadline(trend) {
  const { current_rank, prev_rank, rank_change, total_reps, cash_change_pct, period_label } = trend;

  if (prev_rank === null || prev_rank === undefined) {
    return {
      title: "Welcome to the leaderboard!",
      subtitle: `You're starting at #${current_rank} of ${total_reps} ${period_label}. Let's climb!`,
      tone: "neutral",
    };
  }

  if (rank_change > 0) {
    return {
      title: `You moved from #${prev_rank} → #${current_rank} ${period_label}!`,
      subtitle: `${rank_change === 1 ? "1 spot up" : `${rank_change} spots up`}${cash_change_pct > 0 ? ` • +${cash_change_pct}% cash` : ""}. Keep it up!`,
      tone: "up",
    };
  }

  if (rank_change < 0) {
    return {
      title: `You're at #${current_rank} ${period_label} (was #${prev_rank})`,
      subtitle: `Down ${Math.abs(rank_change)} ${Math.abs(rank_change) === 1 ? "spot" : "spots"}. Time to push — you've got this!`,
      tone: "down",
    };
  }

  return {
    title: `Holding strong at #${current_rank} of ${total_reps} ${period_label}`,
    subtitle: cash_change_pct > 0
      ? `Cash up +${cash_change_pct}%. Stay consistent!`
      : "Same rank, fresh week. Push for the top!",
    tone: "neutral",
  };
}

const TONE_CLASSES = {
  up: {
    bg: "bg-gradient-to-r from-emerald-600/30 via-emerald-500/20 to-transparent border-emerald-500/40",
    icon: "bg-emerald-500/30 text-emerald-300",
    title: "text-emerald-100",
    Trend: TrendingUp,
  },
  down: {
    bg: "bg-gradient-to-r from-amber-600/20 via-amber-500/10 to-transparent border-amber-500/30",
    icon: "bg-amber-500/20 text-amber-300",
    title: "text-amber-100",
    Trend: TrendingDown,
  },
  neutral: {
    bg: "bg-gradient-to-r from-blue-600/20 via-blue-500/10 to-transparent border-blue-500/30",
    icon: "bg-blue-500/20 text-blue-300",
    title: "text-blue-100",
    Trend: Sparkles,
  },
};

export function MyRankTrendBanner() {
  const [trend, setTrend] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchTrend = async () => {
      try {
        const res = await fetch(`${API}/leaderboard/my-rank-trend?days=7`, { credentials: "include" });
        if (res.ok) setTrend(await res.json());
      } catch (e) {
        console.error("Failed to fetch rank trend:", e);
      }
    };
    fetchTrend();

    // Check dismissal in session storage so it reappears next session
    const todayKey = `rank-banner-dismissed-${new Date().toDateString()}`;
    if (sessionStorage.getItem(todayKey) === "true") {
      setDismissed(true);
    }
  }, []);

  if (!trend?.available || dismissed) return null;

  const { title, subtitle, tone } = buildHeadline(trend);
  const cls = TONE_CLASSES[tone];
  const TrendIcon = cls.Trend;

  const handleDismiss = () => {
    setDismissed(true);
    const todayKey = `rank-banner-dismissed-${new Date().toDateString()}`;
    sessionStorage.setItem(todayKey, "true");
  };

  const handleShare = async () => {
    const text = buildShareText(trend);
    // Prefer the native Web Share API on supporting devices (mostly mobile)
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ text, title: "WarMap Win" });
        return;
      } catch (e) {
        // User cancelled or share failed; fall through to clipboard
        if (e?.name !== "AbortError") {
          console.warn("Web Share failed, falling back to clipboard:", e);
        } else {
          return;
        }
      }
    }
    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Failed to copy share text:", e);
    }
  };

  return (
    <div
      data-testid="my-rank-trend-banner"
      className={`relative ${cls.bg} border rounded-xl p-4 sm:p-5 flex items-center gap-4`}
    >
      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full ${cls.icon} flex items-center justify-center flex-shrink-0`}>
        <Trophy className="w-6 h-6 sm:w-7 sm:h-7" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`flex items-center gap-2 font-bold text-base sm:text-lg ${cls.title}`}>
          <TrendIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="truncate">{title}</span>
        </div>
        <div className="text-xs sm:text-sm text-gray-300 mt-0.5">
          {subtitle}
        </div>
      </div>
      <button
        type="button"
        onClick={handleShare}
        className={`text-xs sm:text-sm flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 transition-all flex-shrink-0 ${
          copied ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "text-gray-300 hover:text-white hover:bg-white/10"
        }`}
        aria-label="Copy share text to clipboard"
        data-testid="share-rank-banner-btn"
      >
        {copied ? (
          <>
            <Check className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Copied!</span>
          </>
        ) : (
          <>
            <Share2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Share</span>
          </>
        )}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
        aria-label="Dismiss banner"
        data-testid="dismiss-rank-banner-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
